import { useState, useEffect, useRef, useCallback } from 'react';
import { getDayPlans } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Calendar, Loader2, Navigation, Eye, AlertCircle, RefreshCw } from 'lucide-react';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || 'AIzaSyDh-7ataCAYsNSJkCAvGUwkTXKxE744Gj0';

export const TripMap = ({ tripId, trip }) => {
  const [dayPlans, setDayPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const directionsRendererRef = useRef(null);

  useEffect(() => {
    getDayPlans(tripId).then(r => { 
      setDayPlans(r.data); 
      setLoading(false); 
    }).catch(() => setLoading(false));
  }, [tripId]);

  // Load Google Maps script
  useEffect(() => {
    if (!MAPS_KEY) {
      setMapError('Clé Google Maps non configurée');
      return;
    }

    // Check if already loaded
    if (window.google?.maps?.Map) {
      setMapLoaded(true);
      return;
    }

    // Check if script is already loading
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Wait for existing script to load
      const checkLoaded = setInterval(() => {
        if (window.google?.maps?.Map) {
          clearInterval(checkLoaded);
          setMapLoaded(true);
        }
      }, 100);
      return () => clearInterval(checkLoaded);
    }

    // Create unique callback name to avoid conflicts
    const callbackName = `initGoogleMaps_${Date.now()}`;
    
    window[callbackName] = () => {
      setMapLoaded(true);
      delete window[callbackName];
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places,geometry&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    
    script.onerror = () => {
      setMapError('Erreur chargement Google Maps API');
      delete window[callbackName];
    };
    
    document.head.appendChild(script);

    return () => {
      if (window[callbackName]) delete window[callbackName];
    };
  }, []);

  // Initialize map when ready
  useEffect(() => {
    if (!mapLoaded || !mapContainerRef.current) return;
    if (mapInstanceRef.current) {
      setMapReady(true);
      return;
    }
    
    if (!window.google?.maps?.Map) return;

    // Wait for container to be properly rendered
    const initMap = () => {
      const container = mapContainerRef.current;
      if (!container || container.offsetWidth === 0) {
        requestAnimationFrame(initMap);
        return;
      }

      try {
        const center = { lat: 48.8566, lng: 2.3522 }; // Paris default
        
        mapInstanceRef.current = new window.google.maps.Map(container, {
          zoom: 12,
          center: center,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          styles: [
            { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
            { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
          ]
        });

        directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
          map: mapInstanceRef.current,
          suppressMarkers: true,
          polylineOptions: { 
            strokeColor: '#3A86FF', 
            strokeWeight: 4, 
            strokeOpacity: 0.8 
          }
        });

        setMapReady(true);

        // Geocode trip destination
        if (trip?.destination) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: trip.destination }, (results, status) => {
            if (status === 'OK' && results[0]) {
              mapInstanceRef.current.setCenter(results[0].geometry.location);
              mapInstanceRef.current.setZoom(13);
            }
          });
        }
      } catch (e) {
        setMapError(`Erreur: ${e.message}`);
      }
    };

    // Small delay to ensure DOM is ready
    setTimeout(initMap, 100);
  }, [mapLoaded, trip]);

  // Update markers when selection changes
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !window.google?.maps) return;
    
    const map = mapInstanceRef.current;
    const dr = directionsRendererRef.current;

    // Clear previous
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    if (dr) dr.setDirections({ routes: [] });

    // Get locations
    let locations = [];
    if (selectedDay) {
      const dp = dayPlans.find(d => d.day_plan_id === selectedDay);
      if (dp) {
        (dp.activities || []).forEach(a => {
          if (a.location) {
            locations.push({ 
              title: a.title, 
              location: a.location, 
              block: a.time_block,
              time: a.start_time 
            });
          }
        });
      }
    } else {
      // Global: all locations
      dayPlans.forEach(dp => {
        (dp.activities || []).forEach(a => {
          if (a.location && !locations.find(l => l.location === a.location)) {
            locations.push({ 
              title: a.title, 
              location: a.location, 
              date: dp.date, 
              block: a.time_block 
            });
          }
        });
      });
    }

    if (locations.length === 0) {
      console.log('No locations to display');
      return;
    }

    console.log(`Displaying ${locations.length} locations`);
    const geocoder = new window.google.maps.Geocoder();
    const bounds = new window.google.maps.LatLngBounds();
    const blockColors = { morning: '#F59E0B', afternoon: '#3A86FF', evening: '#8B5CF6' };
    const waypoints = [];

    locations.forEach((loc, i) => {
      geocoder.geocode({ address: loc.location }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const pos = results[0].geometry.location;
          bounds.extend(pos);
          waypoints.push({ pos, index: i });

          const marker = new window.google.maps.Marker({
            position: pos,
            map,
            title: loc.title,
            label: { 
              text: String(i + 1), 
              color: '#FFF', 
              fontWeight: 'bold', 
              fontSize: '12px' 
            },
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: blockColors[loc.block] || '#3A86FF',
              fillOpacity: 1,
              strokeColor: '#FFF',
              strokeWeight: 2,
              scale: 16,
            }
          });

          const info = new window.google.maps.InfoWindow({
            content: `<div style="font-family:system-ui;padding:8px;max-width:220px">
              <strong style="font-size:14px;color:#1E293B">${loc.title}</strong><br/>
              <span style="font-size:12px;color:#64748B">${loc.location}</span>
              ${loc.date ? `<br/><span style="font-size:11px;color:#3A86FF">${new Date(loc.date).toLocaleDateString('fr-FR',{weekday:'short',day:'numeric',month:'short'})}</span>` : ''}
            </div>`
          });
          marker.addListener('click', () => info.open(map, marker));
          markersRef.current.push(marker);

          // Fit bounds after all geocoded
          if (markersRef.current.length === locations.length) {
            if (locations.length > 1) {
              map.fitBounds(bounds, 50);
            }
            
            // Draw route
            if (waypoints.length >= 2) {
              const sorted = waypoints.sort((a, b) => a.index - b.index);
              const directionsService = new window.google.maps.DirectionsService();
              directionsService.route({
                origin: sorted[0].pos,
                destination: sorted[sorted.length - 1].pos,
                waypoints: sorted.slice(1, -1).map(w => ({ location: w.pos, stopover: true })),
                travelMode: window.google.maps.TravelMode.DRIVING,
                optimizeWaypoints: false
              }, (result, status) => {
                if (status === 'OK' && dr) {
                  dr.setDirections(result);
                }
              });
            }
          }
        }
      });
    });
  }, [selectedDay, dayPlans, mapReady]);

  if (!MAPS_KEY) {
    return (
      <div className="widget-card p-10 text-center">
        <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
        <p className="text-sm text-[#94A3B8]">Google Maps non configuré</p>
      </div>
    );
  }

  if (mapError) {
    return (
      <div className="rounded-2xl border border-[#E2E8F0]/40 bg-white p-10 text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-sm text-[#94A3B8]">{mapError}</p>
        <Button onClick={() => window.location.reload()} variant="outline" className="mt-4 rounded-xl">
          <RefreshCw className="w-4 h-4 mr-2" /> Recharger
        </Button>
      </div>
    );
  }

  if (loading) return <div className="h-[500px] bg-white rounded-2xl animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="trip-map">
      {/* Day selector */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setSelectedDay(null)}
          className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
            selectedDay === null
              ? 'bg-[#3A86FF] text-white shadow-md'
              : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#3A86FF]/30'
          }`}
          data-testid="map-global-btn"
        >
          <Navigation className="w-3.5 h-3.5 inline mr-1" /> Vue globale
        </button>
        {dayPlans.map(dp => {
          const d = new Date(dp.date);
          const hasActivities = (dp.activities || []).some(a => a.location);
          return (
            <button
              key={dp.day_plan_id}
              onClick={() => setSelectedDay(dp.day_plan_id)}
              className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                selectedDay === dp.day_plan_id
                  ? 'bg-[#3A86FF] text-white shadow-md'
                  : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#3A86FF]/30'
              } ${!hasActivities ? 'opacity-40' : ''}`}
            >
              {d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}
              {hasActivities && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-current inline-block" />}
            </button>
          );
        })}
      </div>

      {/* Map container */}
      <div className="rounded-2xl border border-[#E2E8F0]/40 bg-white shadow-sm overflow-hidden relative" style={{ height: '480px' }}>
        {!mapLoaded ? (
          <div className="h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#F8FAFC] to-[#EBF2FF]">
            <Loader2 className="w-8 h-8 animate-spin text-[#3A86FF] mb-3" />
            <p className="text-sm text-[#64748B]">Chargement de Google Maps...</p>
          </div>
        ) : (
          <div 
            ref={mapContainerRef} 
            style={{ width: '100%', height: '480px', position: 'absolute', top: 0, left: 0 }} 
          />
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px] text-[#94A3B8]">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#F59E0B]" /> Matin</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#3A86FF]" /> Après-midi</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#8B5CF6]" /> Soir</span>
        <span className="ml-auto flex items-center gap-1">
          <Eye className="w-3 h-3" /> 
          {selectedDay ? 'Vue journée' : `Vue globale (${dayPlans.reduce((acc, dp) => acc + (dp.activities?.filter(a => a.location)?.length || 0), 0)} lieux)`}
        </span>
      </div>
    </div>
  );
};
